
import { Switch, Form, Input, Select, InputNumber } from "antd"
import TextArea from "antd/es/input/TextArea"
import { useEffect, useMemo, useState } from "react"
import { getAllWidgets } from "../../../utils/formsHelper"
import { useTranslation } from "react-i18next"

const dateFormats = [
    { label: 'DD-MM-YYYY', value: 'DD-MM-YYYY' },
    { label: 'MM-DD-YYYY', value: 'MM-DD-YYYY' },
    { label: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
    { label: 'MM-YYYY', value: 'MMMM-YYYY' },
    { label: 'YYYY', value: 'YYYY' },
    { label: 'MMM', value: 'MMM' },

]

const DateForm = ({ formValues, form, formWidgets }) => {
    const [dateWidgets, setDateWidgets] = useState([])
    const [numberWidgets, setNumberWidgets] = useState([])
    const { t } = useTranslation();

    useEffect(() => {
        const widgets = formWidgets ? getAllWidgets(formWidgets) : []
        const toOption = (widget) => {
            let label = widget.label
            if (widget.sectionLabel) {
                label += ` (${widget.sectionLabel})`
            }
            return { ...widget, label, value: widget.id }
        }
        const selectable = widgets
            .filter(widget => !widget.parentTableId)
            .filter(widget => widget.id !== formValues?.id)
        setDateWidgets(selectable.filter(widget => widget.formType === "dateForm").map(toOption))
        setNumberWidgets(selectable.filter(widget => widget.formType === "numberForm").map(toOption))
    }, [formWidgets, formValues?.id])

    // Widgets saved before the count could be dynamic have no source stored, and
    // for them the count has always been the static number.
    const countSource = formValues?.relativeDateCountSource || "static"

    const ruleText = useMemo(() => {
        if (!formValues?.relativeDateEnabled) return ""
        const direction = formValues?.relativeDateDirection
        const unit = formValues?.relativeDateUnit
        const reference = formValues?.relativeDateReference
        if (!direction || !unit || !reference) return ""
        const refLabel = dateWidgets.find(w => w.value === reference)?.label || "Selected date"
        // A dynamic count is only known once the form is filled, so the preview
        // names the field it will be read from instead of a number.
        if (countSource === "dynamic") {
            const countField = formValues?.relativeDateCountField
            if (!countField) return ""
            const countLabel = numberWidgets.find(w => w.value === countField)?.label || "Selected number"
            return `${countLabel} ${unit}(s) ${direction} ${refLabel}`
        }
        const count = formValues?.relativeDateCount
        if (count === undefined || count === null || count === "") return ""
        const unitLabel = Number(count) === 1 ? unit : `${unit}s`
        return `${count} ${unitLabel} ${direction} ${refLabel}`
    }, [
        formValues?.relativeDateEnabled,
        countSource,
        formValues?.relativeDateCount,
        formValues?.relativeDateCountField,
        formValues?.relativeDateDirection,
        formValues?.relativeDateUnit,
        formValues?.relativeDateReference,
        dateWidgets,
        numberWidgets
    ])


    return (
        <>
            <Form.Item name="label" label={t("Label")}>
                <Input placeholder={t("Enter label")} />
            </Form.Item>

            <Form.Item name="placeholder" label={t("Placeholder")}>
                <Input placeholder={t("Enter placeholder")} />
            </Form.Item>

            <Form.Item name="tooltip" label={t("Tooltip")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            {
                formValues && formValues.tooltip &&
                <Form.Item label="" name="helpText">
                    <TextArea rows={2} placeholder={t("Enter help text")} />
                </Form.Item>
            }

            <Form.Item name="mandatory" label={t("Mandatory")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="readOnly" label={t("Read Only")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>
            <Form.Item name="hideInitially" label={t("Hide Initially")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>
            {/* <Form.Item name="sendReminder" label={t("Send Reminder")} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>
            {
                formValues?.sendReminder ?
                    <Form.Item name="dayCountForReminder" label={t("Day Count For Reminder")}>
                        <Input placeholder={t("Enter number of days before the expiry to send notification")} />
                    </Form.Item>
                    : null
            } */}
            <Form.Item
                name="isToday"
                label={t("Is Today")}
                className="horizontal-form-item"
            >
                <Switch size="small" />
            </Form.Item>
            <Form.Item name="dateFormat" label={t("Date Format")} >
                <Select options={dateFormats} />
            </Form.Item>
            <Form.Item name="relativeDateEnabled" label={t("Relative Date")} className="horizontal-form-item">
                <Switch size="small" onChange={(val) => {
                    if (!val) {
                        form.setFieldValue("relativeDateReference", null)
                        form.setFieldValue("relativeDateDirection", null)
                        form.setFieldValue("relativeDateCountSource", "static")
                        form.setFieldValue("relativeDateCount", null)
                        form.setFieldValue("relativeDateCountField", null)
                        form.setFieldValue("relativeDateUnit", null)
                    }
                }} />
            </Form.Item>

            {
                formValues?.relativeDateEnabled ?
                    <>
                        <Form.Item name="relativeDateReference" label={t("Base Date Field")}>
                            <Select
                                showSearch
                                allowClear
                                placeholder={t("Select a date field")}
                                optionFilterProp="label"
                                options={dateWidgets}
                            />
                        </Form.Item>
                        <Form.Item name="relativeDateDirection" label={t("Direction")}>
                            <Select
                                options={[
                                    { label: t("After"), value: "after" },
                                    { label: t("Before"), value: "before" }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name="relativeDateCountSource" label={t("Count Type")}>
                            <Select
                                options={[
                                    { label: t("Static"), value: "static" },
                                    { label: t("Dynamic"), value: "dynamic" }
                                ]}
                                onChange={(val) => {
                                    // Only one of the two can apply, so drop the value
                                    // that belongs to the source being turned off.
                                    if (val === "dynamic") {
                                        form.setFieldValue("relativeDateCount", null)
                                    } else {
                                        form.setFieldValue("relativeDateCountField", null)
                                    }
                                }}
                            />
                        </Form.Item>
                        {
                            countSource === "dynamic" ?
                                <Form.Item name="relativeDateCountField" label={t("Count Field")}>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder={t("Select a number field")}
                                        optionFilterProp="label"
                                        options={numberWidgets}
                                    />
                                </Form.Item>
                                :
                                <Form.Item name="relativeDateCount" label={t("Count")}>
                                    <InputNumber min={0} placeholder={t("Enter count")} style={{ width: "100%" }} />
                                </Form.Item>
                        }
                        <Form.Item name="relativeDateUnit" label={t("Unit")}>
                            <Select
                                options={[
                                    { label: t("Day"), value: "day" },
                                    { label: t("Week"), value: "week" },
                                    { label: t("Month"), value: "month" },
                                    { label: t("Year"), value: "year" }
                                ]}
                            />
                        </Form.Item>
                        <Form.Item label={t("Rule Preview")}>
                            <Input value={ruleText || t("Complete the rule to preview")} disabled />
                        </Form.Item>
                    </>
                    : null
            }


        </>
    )
}

export default DateForm
